import { translatedError } from '@/utils/errors';
import { hasMinimumLength, stringToCheck } from '@/utils/resultsHelper';
import { formatDate } from '@/utils/dateHelper';
import parseNumericValue from '@/utils/numberHelper';
import fieldNames, { dictionaryDateTypes, dictionaryNumberTypes } from '@/constants/fields';
import {
  cohortArrayOperators,
  cohortExistOperators,
  cohortFilters,
  cohortJoinOperators,
  cohortRangeOperators,
} from './constants';
import { uniqueDictionariesInClauses } from '../state/shared';
import {
  cohortDateTimeValue,
  cohortDateValue,
  cohortTimeValue,
  isDateString,
  isTimeString,
} from './datetimeHelper';

const errorPrefix = (isRead = false) => (isRead ? 'read.cohort.validation' : 'save.cohort.validation');

const errorTitle = (suffix, isRead = false) => {
  const prefix = errorPrefix(isRead);
  return isRead ? `${prefix}.invalid` : `${prefix}.${suffix}.title`;
};

const errorMessage = (suffix, isRead = false) => `${errorPrefix(isRead)}.${suffix}`;

export const cohortValidationError = (context, suffix, isRead = false, fields = [fieldNames.FIELD]) => (
  translatedError(context, errorTitle(suffix, isRead), errorMessage(suffix, isRead), fields)
);

const saveCohortError = (context, suffix, message, fields = [fieldNames.FIELD]) => (
  translatedError(context, errorTitle(suffix), message, fields)
);

const hasValidOperator = (filter) => {
  const operators = Object.values(cohortFilters[filter.field.type]);
  return filter.operator && operators.includes(filter.operator);
};

const addOperatorError = (filter, errors, context, isRead) => {
  const suffix = (!filter.operator && !isRead) ? 'required.filter.field' : 'mismatch.filter.operator';
  errors.push(cohortValidationError(context, suffix, isRead, [fieldNames.OPERATOR]));
};

const validateJoinField = (filter, errors, query, context) => {
  const { type, dictionary } = filter.join;

  if (filter.field.type !== type) {
    errors.push(cohortValidationError(context, 'mismatch.join', false, [fieldNames.JOIN]));
  }

  if (!uniqueDictionariesInClauses(query).some((d) => d.code === dictionary.code)) {
    errors.push(cohortValidationError(context, 'required.dictionary.join', false, [fieldNames.JOIN]));
  }
};

const validateJoin = (filter, errors, query, context) => {
  if (!hasMinimumLength(filter.join?.name)) {
    errors.push(cohortValidationError(context, 'required.field.join', false, [fieldNames.JOIN]));
  } else {
    validateJoinField(filter, errors, query, context);
  }
};

const validateValue = (filter, errors, context) => {
  if (filter.values.length === 0 || filter.values.some((v) => !hasMinimumLength(v))) {
    errors.push(cohortValidationError(context, 'required.filter.values', false, [fieldNames.VALUE]));
  }
};

const validateRange = (filter, values, errors, context) => {
  if (!Object.values(cohortRangeOperators).includes(filter.operator)) {
    return;
  }

  if (values.length !== 2) {
    const error = saveCohortError(context, 'exact.filter.values', 'validation.exact.length', [fieldNames.VALUE]);
    error.translation.messageArgs = { value: 2 };
    errors.push(error);
  } else {
    const [lowerBound, upperBound] = values;
    if (lowerBound > upperBound) {
      errors.push(saveCohortError(context, 'invalid.range.filter', 'validation.invalid.range'));
    }
  }
};

const validateDates = (filter, errors, context, isRead) => {
  validateValue(filter, errors, context);
  const values = filter.values.map((v) => cohortDateValue(v));

  if (values.some((v) => v === null)) {
    if (isRead) {
      addOperatorError(filter, errors, context, isRead);
    } else {
      errors.push(saveCohortError(context, 'not.date.filter', 'validation.date'));
    }
  } else { validateRange(filter, values, errors, context); }
};

const validateDateTimes = (filter, errors, context) => {
  validateValue(filter, errors, context);
  const values = filter.values.map((v) => cohortDateTimeValue(v));

  if (values.some((v) => v === null)) {
    errors.push(saveCohortError(context, 'not.datetime.filter', 'validation.datetime'));
  } else { validateRange(filter, values, errors, context); }
};

const validateTimes = (filter, errors, context) => {
  validateValue(filter, errors, context);
  const values = filter.values.map((v) => cohortTimeValue(v));

  if (values.some((v) => v === null)) {
    errors.push(saveCohortError(context, 'not.time.filter', 'validation.time'));
  } else { validateRange(filter, values, errors, context); }
};

const invalidNumber = (filter, context) => {
  const suffix = filter.field.type === 'decimal' ? 'not.decimal.filter' : 'not.numeric.filter';
  return saveCohortError(context, suffix, 'validation.invalid.range');
};

const validateNumbers = (filter, errors, context, isRead) => {
  validateValue(filter, errors, context);
  const values = filter.values.map((v) => parseNumericValue(v, null, filter.field.type));

  if (values.some((v) => v === null)) {
    if (isRead) {
      addOperatorError(filter, errors, context, true);
    } else { errors.push(invalidNumber(filter, context)); }
  } else { validateRange(filter, values, errors, context); }
};

const isValidFilterField = (filter) => filter.field && filter.field.cohort_filter;

const doesOperatorExist = (filter) => Object.values(cohortExistOperators).includes(filter.operator);

const booleanValue = (value) => {
  if (typeof value === 'boolean') {
    return value;
  }

  const lowercasedValue = (typeof value === 'string') ? value.toLowerCase() : value;

  if (typeof value === 'number') {
    if (value === 1) {
      return true;
    } if (value === 0) {
      return false;
    }
    return null;
  }

  if (['true', 't', 1].includes(lowercasedValue)) return true;
  if (['false', 'f', 0].includes(lowercasedValue)) return false;

  return null;
};

const validateBooleans = (filter, errors, context, isRead) => {
  validateValue(filter, errors, context);
  const values = filter.values.map((v) => booleanValue(v));

  if (values.some((v) => v === null)) {
    errors.push(cohortValidationError(context, 'mismatch.filter.operator', isRead));
  } else { validateRange(filter, values, errors, context); }
};

const validateDateTimeValues = (filter, errors, context, isRead) => {
  if (filter.field.type === dictionaryDateTypes.DATE) {
    validateDates(filter, errors, context, isRead);
  }
  if (filter.field.type === dictionaryDateTypes.DATETIME) {
    validateDateTimes(filter, errors, context);
  }
  if (filter.field.type === dictionaryDateTypes.TIME) {
    validateTimes(filter, errors, context);
  }
};

const validateValues = (filter, errors, query, context, isRead) => {
  if (Object.values(cohortJoinOperators).includes(filter.operator)) {
    validateJoin(filter, errors, query, context);
  } else if (Object.values(dictionaryNumberTypes).includes(filter.field.type)) {
    validateNumbers(filter, errors, context, isRead);
  } else if (Object.values(dictionaryDateTypes).includes(filter.field.type)) {
    validateDateTimeValues(filter, errors, context, isRead);
  } else if (filter.field.type === 'boolean') {
    validateBooleans(filter, errors, context, isRead);
  } else {
    validateValue(filter, errors, context);
  }
};

const validateFilter = (filter, errors, clause, query, isRead) => {
  const context = {
    entity: 'filter',
    queryUUID: query.uuid,
    queryName: query.name,
    clauseUUID: clause.uuid,
    dictionaryCode: clause?.dictionary?.code,
    filterUUID: filter.uuid,
  };

  if (!isValidFilterField(filter)) {
    errors.push(cohortValidationError(context, 'required.filter.field', isRead));
  } else if (!hasValidOperator(filter)) {
    addOperatorError(filter, errors, context, isRead);
  } else if (!doesOperatorExist(filter)) {
    validateValues(filter, errors, query, context, isRead);
  }
};

export const translatedOperator = (operator, intl) => {
  if (!operator) {
    return {
      text: intl.formatMessage({ id: 'operator.invalid' }),
      description: intl.formatMessage({ id: 'operator.invalid' }),
    };
  }

  const sanitised = operator.toLowerCase().replace(/_/g, '.');
  return {
    text: intl.formatMessage({ id: `operator.${sanitised}` }),
    description: intl.formatMessage({ id: `operator.description.${sanitised}` }),
  };
};

const mapDateTimes = (fieldType, values) => {
  if (fieldType === dictionaryDateTypes.DATE) {
    return values.map((v) => (isDateString(v) ? formatDate(cohortDateValue(v), {
      month: 'long',
      year: 'numeric',
      day: 'numeric',
    }) : v));
  }

  if (fieldType === dictionaryDateTypes.DATETIME) {
    return values.map((v) => (isDateString(v) ? formatDate(cohortDateTimeValue(v), {
      month: 'long',
      year: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    }) : v));
  }

  if (fieldType === dictionaryDateTypes.TIME) {
    return values.map((v) => (isTimeString(v) ? formatDate(cohortTimeValue(v), {
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
    }) : v));
  }

  return values;
};

const mappedValues = (fieldType, lookups, values) => {
  if (lookups.length > 0) {
    return values.map((v) => (lookups.find((lookup) => lookup.name === v) || { description: v }).description);
  }

  if (Object.values(dictionaryDateTypes).includes(fieldType)) {
    return mapDateTimes(fieldType, values);
  }

  return values;
};

const formatLookupValues = (fieldType, operator, values, lookups, intl) => {
  const displayValues = mappedValues(fieldType, lookups, values);
  if (Object.values(cohortArrayOperators).includes(operator)) {
    return displayValues.join(', ');
  }

  if (Object.values(cohortRangeOperators).includes(operator)) {
    return intl.formatMessage(
      { id: 'operator.formatted.in.range' },
      { from: displayValues[0], to: displayValues[1] },
    );
  }

  return displayValues;
};

const formatJoinValues = (fieldType, operator, join, intl) => {
  if (!Object.values(cohortJoinOperators).includes(operator)) { return null; }

  if (join) {
    const { name, dictionary } = join;
    return intl.formatMessage({ id: 'operator.formatted.join.range' }, { field: name, dictionary: dictionary.name });
  }

  return intl.formatMessage({ id: 'operator.formatted.join.unknown' });
};

export const formatFilterValues = (fieldType, operator, values, lookups, join, intl) => (
  formatJoinValues(fieldType, operator, join, intl) || formatLookupValues(fieldType, operator, values, lookups, intl)
);

export const uniqueDictionaryJoins = ({ filters }) => {
  const joins = filters.filter((f) => Object.values(cohortJoinOperators).includes(f.operator) && f.join);
  return joins.filter((v, i, a) => a.findIndex(({ join }) => (join.dictionary.code === v.join.dictionary.code)) === i);
};

export const hasJoinsOnDictionary = ({ filters }, dictionary) => (
  filters.some(({ operator, join }) => (
    Object.values(cohortJoinOperators).includes(operator) && dictionary.code === join?.dictionary?.code))
);

export const parseNumericForNumberInput = (value, fieldType = 'integer') => {
  if (fieldType === dictionaryNumberTypes.INTEGER) {
    const parsedIntValue = parseInt(stringToCheck(value), 10);
    if (!Number.isNaN(parsedIntValue)) {
      return parsedIntValue.toString();
    }
  }

  if (fieldType === dictionaryNumberTypes.DECIMAL) {
    const parsedFloatValue = parseFloat(stringToCheck(value));
    if (!Number.isNaN(parsedFloatValue)) {
      return value.toString();
    }
  }

  return '';
};

export default validateFilter;
